// Session-based "incoming transmission" notification. Once per session, never on forms/admin.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, Radio, GraduationCap, Briefcase } from "lucide-react";

const ELIGIBLE = ["/", "/village", "/about", "/campaigns", "/issues"];
const isEligible = (p) => ELIGIBLE.includes(p) || p.startsWith("/issues/") || p.startsWith("/campaigns/");

export const IncomingTransmission = () => {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState("hidden"); // hidden | visible | choosing

  useEffect(() => {
    try { if (sessionStorage.getItem("blv_tx_seen")) return; } catch {}
    if (!isEligible(pathname)) return;
    const t = setTimeout(() => setPhase("visible"), 10000);
    return () => clearTimeout(t);
  }, [pathname]);

  const seen = () => { try { sessionStorage.setItem("blv_tx_seen", "1"); } catch {} };
  const dismiss = () => { seen(); setPhase("hidden"); };

  return (
    <AnimatePresence>
      {phase !== "hidden" && (
        <motion.div
          className="fixed bottom-5 right-5 z-[70] w-[calc(100%-2.5rem)] max-w-sm"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
          transition={{ duration: reduced ? 0 : 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          data-testid="incoming-transmission"
          role="dialog" aria-label="Incoming transmission"
        >
          <div className="relative overflow-hidden border border-gold/40 bg-onyx/95 shadow-[0_0_50px_rgba(0,0,0,0.7),0_0_25px_rgba(201,162,39,0.12)] backdrop-blur-md">
            {!reduced && (
              <motion.div
                className="pointer-events-none absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent"
                initial={{ top: 0 }} animate={{ top: "100%" }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
              />
            )}
            <div className="grain-dark pointer-events-none absolute inset-0 opacity-60" />
            <button onClick={dismiss} aria-label="Dismiss transmission" data-testid="transmission-dismiss-btn"
              className="absolute right-2.5 top-2.5 z-10 p-1 text-silver/50 transition-colors hover:text-goldlight">
              <X size={14} />
            </button>
            <div className="relative p-5">
              <div className="flex items-center gap-2.5">
                <span className={`h-2 w-2 rounded-full bg-gold ${reduced ? "" : "animate-pulse"}`} aria-hidden="true" />
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-gold">Incoming transmission</p>
              </div>
              {phase === "visible" ? (
                <>
                  <div className="mt-3 space-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-silver/75">
                    <p>Source: <span className="text-cream">The Architect</span></p>
                    <p>Clearance: <span className="text-cream">Industry / Education</span></p>
                  </div>
                  <button onClick={() => setPhase("choosing")} data-testid="intercept-message-btn"
                    className="btn-sweep mt-4 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-5 py-2.5 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
                    <Radio size={13} /> Intercept Message
                  </button>
                </>
              ) : (
                <>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-silver/75">Select your network to decode:</p>
                  <div className="mt-3 grid gap-2">
                    <Link to="/transmission/education" onClick={seen} data-testid="transmission-education-link"
                      className="group flex items-center gap-3 border border-silver/25 bg-charcoal/70 px-4 py-3 transition-colors hover:border-gold/60">
                      <GraduationCap size={16} className="text-gold" />
                      <span className="font-head text-xs font-extrabold uppercase tracking-[0.15em] text-cream group-hover:text-goldlight">Education Network</span>
                    </Link>
                    <Link to="/transmission/industry" onClick={seen} data-testid="transmission-industry-link"
                      className="group flex items-center gap-3 border border-silver/25 bg-charcoal/70 px-4 py-3 transition-colors hover:border-gold/60">
                      <Briefcase size={16} className="text-gold" />
                      <span className="font-head text-xs font-extrabold uppercase tracking-[0.15em] text-cream group-hover:text-goldlight">Industry Network</span>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
